import { DeviceAsset, LineAsset, UserAsset } from './dashboard.model';
import {
  OrderPriceSummary,
  OrderPriceSummaryItem,
  ShoppingCartData,
  ShoppingCartItem,
  ShoppingCartPriceItem
} from './shopping-cart.model';
import {
  AccessoryItemData,
  CART_ITEM_TYPE_ACCESSORY,
  CART_ITEM_TYPE_DEVICE,
  CART_ITEM_TYPE_FEATURE,
  CART_ITEM_TYPE_PLAN,
  DeviceData,
  DeviceItemDevice,
  ShoppingFilterData,
  ShoppingFilterItem
} from './shopping.model';

// ### Constant(s) - move to separate constants.model.ts file?
export const MULTI_SIM_TYPES: string[] = [
  '1DML',
  '1LMD',
  'device_with_line',
  'device',
  'line',
  'line_with_ref_device',
  '1LMD_with_ref_device'
];

// ### General Functions - could be used anywhere
export function fieldValueIsPresent(field: string | undefined): string | undefined {
  return field && (field !== '' || field !== undefined || field !== null)
    ? field?.toString()
    : 'N/A';
}

// Mobile returns carrier logo URLs with a 'tiny' size variant; offcanvas and asset-selection views require 'original'.
export function carrierLogoUrl(url: string | null | undefined): string | undefined {
  return url?.replace(/\/(tiny|thumb|medium|small)\//, '/original/') ?? undefined;
}

// ### My Assets Functions
export function generateAssetImageUrl(type: string): string {
  const url: string = 'assets/images/devices/';

  switch (type.toLocaleLowerCase()) {
    case 'data card':
      return `${url}data_card_placeholder.png`;
    case 'desktop':
      return `${url}desktop_placeholder.png`;
    case 'laptop':
      return `${url}laptop_placeholder.png`;
    case 'line':
      return `${url}line_placeholder.png`;
    case 'machine-to-machine':
      return `${url}machine2machine_placeholder.png`;
    case 'pager':
      return `${url}pager_placeholder.png`;
    case 'phone':
      return `${url}phone_placeholder.png`;
    case 'router':
      return `${url}router_placeholder.png`;
    case 'satellite phone':
      return `${url}satellitephone_placeholder.png`;
    case 'signal booster':
      return `${url}signalbooster_placeholder.png`;
    case 'smart phone':
      return `${url}smart_phone_placeholder.png`;
    case 'tablet':
      return `${url}tablet_placeholder.png`;
    case 'wearable':
      return `${url}wearable_placeholder.png`;
    default:
      return `${url}other_placeholder.png`;
  }
}

export function hasLineId(line: LineAsset | undefined): boolean {
  return Boolean(line?.id);
}

export function hasDeviceId(device: DeviceAsset | undefined): boolean {
  return Boolean(device?.id);
}

export function hasDeviceCatalogId(device: DeviceAsset | undefined): boolean {
  return Boolean(device?.device_catalog_id);
}

export function generateAssetIndex(asset: UserAsset): string | undefined {
  let id: string = '';
  if (hasLineId(asset.line)) id = asset.line.id.toString();
  if (hasDeviceId(asset.device)) id += `d${asset.device.id!.toString()}`;
  return id ? id : undefined;
}

export function constructAssetTitle(asset: UserAsset, include_details: boolean = false): string {
  if (!hasDeviceId(asset.device) && !hasDeviceCatalogId(asset.device))
    return asset.line!.international_number ?? 'Line';

  let device_title: string =
    (asset.device.manufacturer ? `${asset.device.manufacturer} ` : '') + (asset.device.model ?? '');
  if (include_details) {
    device_title += ' ' + asset.device.size;
    if (asset.device.color && asset.device.color.length > 0)
      device_title += ' - ' + asset.device.color;
  }

  return device_title ? device_title : 'Unknown Device';
}

export function constructAdditionalAssetTitle(
  asset: DeviceAsset | LineAsset,
  type: number
): string {
  let additional_device_title: string =
    (asset as DeviceAsset).manufacturer +
    ' ' +
    (asset as DeviceAsset).model +
    ' ' +
    (asset as DeviceAsset).size;

  if ((asset as DeviceAsset)?.color)
    additional_device_title += ' - ' + (asset as DeviceAsset).color;

  return type === 0 ? (asset as LineAsset).international_number! : additional_device_title;
}

export function calculateMultiSimType(asset: UserAsset): string {
  if (asset?.additional_lines && asset?.additional_lines?.length > 0) return MULTI_SIM_TYPES[0];
  else if (asset?.additional_devices && asset?.additional_devices?.length > 0) {
    if (hasDeviceCatalogId(asset.device) && !hasDeviceId(asset.device)) return MULTI_SIM_TYPES[6];
    return MULTI_SIM_TYPES[1];
  } else if (hasDeviceId(asset.device) && hasLineId(asset.line)) return MULTI_SIM_TYPES[2];
  else if (hasDeviceId(asset.device) && !hasLineId(asset.line)) return MULTI_SIM_TYPES[3];
  else if (hasLineId(asset.line) && hasDeviceCatalogId(asset.device)) return MULTI_SIM_TYPES[5];
  else if (!hasDeviceId(asset.device) && hasLineId(asset.line)) return MULTI_SIM_TYPES[4];
  else return 'unknown';
}

export function hasMultiSim(asset: UserAsset): boolean {
  return (asset.additional_devices && asset.additional_devices?.length > 0) ||
    (asset.additional_lines && asset.additional_lines?.length > 0)
    ? true
    : false;
}

// ### Shopping Functions
export function activeFiltersCount(filters: ShoppingFilterData[]): number {
  let count: number = 0;
  filters.forEach((filter: ShoppingFilterData) => {
    filter.items.forEach((filter_item: ShoppingFilterItem) => {
      if (filter_item.active) {
        count++;
      }
    });
  });
  return count;
}

/**
 * @param cart_item: input can be either DeviceItemDevice | PlanType | AccessoryItemData | FeatureType | ShoppingCartItem
 */
export function createShoppingObject(
  cart_item: any,
  cart_type: string,
  image_url?: string,
  is_bp_device?: boolean
): ShoppingCartItem {
  /*
    The first time through the activity flow, "cart_item" is actually a DeviceItemDevice (for the device),
    but any subsequent times through "cart_item" is actually a ShoppingCartItem. That is why the name, description,
    and price objects below all have the undefined catches ("??").
  */

  return {
    id: cart_item.id,
    type: cart_type,
    quantity: cart_type === CART_ITEM_TYPE_ACCESSORY && cart_item.quantity ? cart_item.quantity : 1,
    edit_link_payload: cart_item.edit_link_payload ?? undefined,

    name:
      cart_type === CART_ITEM_TYPE_DEVICE
        ? `${cart_item.manufacturer ?? cart_item.additional_info?.manufacturer} ${
            cart_item.model ?? cart_item.additional_info?.model
          }`
        : cart_item.name_override
          ? cart_item.name_override
          : cart_item.name,

    description:
      cart_type === CART_ITEM_TYPE_DEVICE
        ? `${cart_item.original_color ?? cart_item.additional_info?.color} - ${
            cart_item.size ?? cart_item.additional_info?.size
          }`
        : undefined,

    image_url: [CART_ITEM_TYPE_DEVICE, CART_ITEM_TYPE_ACCESSORY].includes(cart_type)
      ? cart_item.image_url
      : image_url,

    monthly_cost_to_currency:
      cart_type === CART_ITEM_TYPE_DEVICE
        ? (cart_item.price?.monthly?.cost_to_currency ?? cart_item?.monthly_cost_to_currency)
        : cart_type === CART_ITEM_TYPE_PLAN || cart_type === CART_ITEM_TYPE_FEATURE
          ? (cart_item.cost_to_currency ??
            cart_item.cost?.cost_to_currency ??
            cart_item.monthly_cost_to_currency)
          : undefined,

    onetime_cost_to_currency:
      cart_type === CART_ITEM_TYPE_DEVICE
        ? (cart_item.price?.onetime?.cost_to_currency ?? cart_item.onetime_cost_to_currency)
        : cart_type === CART_ITEM_TYPE_ACCESSORY
          ? (cart_item.cost_to_currency ??
            cart_item.cost?.cost_to_currency ??
            cart_item.onetime_cost_to_currency)
          : undefined,

    additional_info: {
      included: cart_item?.included ?? cart_item.additional_info?.included,
      option: cart_item?.name_override ?? cart_item.additional_info?.option,

      size:
        cart_type === CART_ITEM_TYPE_DEVICE
          ? (cart_item.size ?? cart_item.additional_info?.size)
          : undefined,

      model:
        cart_type === CART_ITEM_TYPE_DEVICE
          ? (cart_item.model ?? cart_item.additional_info?.model)
          : undefined,

      category:
        cart_type === CART_ITEM_TYPE_DEVICE
          ? (cart_item.product_category ?? cart_item.additional_info?.category)
          : cart_type === CART_ITEM_TYPE_ACCESSORY
            ? (cart_item.category ?? cart_item.additional_info?.category)
            : undefined,

      category_id: [CART_ITEM_TYPE_DEVICE, CART_ITEM_TYPE_ACCESSORY].includes(cart_type)
        ? (cart_item.category_id ?? cart_item.additional_info?.category_id)
        : undefined,

      manufacturer:
        cart_type === CART_ITEM_TYPE_DEVICE
          ? (cart_item.manufacturer ?? cart_item.additional_info?.manufacturer)
          : undefined,

      color:
        cart_type === CART_ITEM_TYPE_DEVICE
          ? (cart_item.original_color ?? cart_item.additional_info?.color)
          : undefined,

      admin: cart_item?.admin ?? cart_item.additional_info?.admin,
      business_purpose: is_bp_device ? cart_item?.business_purpose : undefined
    }
  };
}

export function createShoppingPriceObject(
  cart_item: AccessoryItemData,
  cart_type: string
): ShoppingCartPriceItem {
  return {
    type: cart_type,
    id: cart_item.id,
    quantity: cart_item.quantity!
  };
}

export function loadPaymentCardImage(cart_type: string): string {
  const card: string = cart_type.replace(' ', '').toLowerCase();
  return `assets/images/payment-cards/${card}.png`;
}

export function loadDeviceImagePlaceholder(category: string, event: any): string {
  const url: string = 'assets/images/devices/';

  switch (category.toLocaleLowerCase()) {
    case 'data card':
      return (event.target.src = `${url}data_card_placeholder.png`);
    case 'desktop':
      return (event.target.src = `${url}desktop_placeholder.png`);
    case 'laptop':
      return (event.target.src = `${url}laptop_placeholder.png`);
    case 'line':
      return (event.target.src = `${url}line_placeholder.png`);
    case 'machine-to-machine':
      return (event.target.src = `${url}machine2machine_placeholder.png`);
    case 'pager':
      return (event.target.src = `${url}pager_placeholder.png`);
    case 'phone':
      return (event.target.src = `${url}phone_placeholder.png`);
    case 'router':
      return (event.target.src = `${url}router_placeholder.png`);
    case 'satellite phone':
      return (event.target.src = `${url}satellitephone_placeholder.png`);
    case 'signal booster':
      return (event.target.src = `${url}signalbooster_placeholder.png`);
    case 'smart phone':
      return (event.target.src = `${url}smart_phone_placeholder.png`);
    case 'tablet':
      return (event.target.src = `${url}tablet_placeholder.png`);
    case 'wearable':
      return (event.target.src = `${url}wearable_placeholder.png`);
    default:
      return (event.target.src = `${url}other_placeholder.png`);
  }
}

export function loadAccessoryImagePlaceholder(category: string, event: any): string {
  const url: string = 'assets/images/accessories/';

  switch (category.toLowerCase()) {
    case 'accessory_bundles':
      return (event.target.src = `${url}accessory_bundles_placeholder.png`);
    case 'adapters_and_cables':
      return (event.target.src = `${url}adapters_cables_placeholder.png`);
    case 'cases':
      return (event.target.src = `${url}cases_placeholder.png`);
    case 'headphones':
      return (event.target.src = `${url}headphones_placeholder.png`);
    case 'other':
      return (event.target.src = `${url}other_placeholder.png`);
    case 'batteries_and_chargers':
      return (event.target.src = `${url}batteries_chargers_placeholder.png`);
    case 'screen_protectors':
      return (event.target.src = `${url}screen_protectors_placeholder.png`);
    default:
      return (event.target.src = `${url}no_image_available.png`);
  }
}

export function stringOnlyHTML(str: string | undefined): boolean {
  if (!str) return false;
  let sanitized_str: string = str.replace(/<[^>]*>/g, '');
  return sanitized_str && sanitized_str !== '' ? false : true;
}

export function determineAssetCatalogInfo(
  asset_device: UserAsset['device'],
  device_data: DeviceData['items']
): [number | undefined, DeviceItemDevice | undefined] {
  let carrier_id: number | undefined = undefined;
  let catalog_device: DeviceItemDevice | undefined = undefined;
  let partial_match = false;

  for (const item of device_data) {
    for (const device of item.devices) {
      if (device.device_catalog_id === asset_device.device_catalog_id) {
        carrier_id = device.carrier_id;
        catalog_device = device;
        break;
      }

      if (partial_match) continue;

      if (
        device.manufacturer === asset_device.manufacturer &&
        device.model === asset_device.model &&
        device.original_color === asset_device.color &&
        device.size === asset_device.size
      ) {
        carrier_id = device.carrier_id;
        catalog_device = device;
        partial_match = true;
      }
    }

    if (catalog_device?.device_catalog_id === asset_device.device_catalog_id) break;
  }

  return [carrier_id, catalog_device];
}

export function getCarrier(line: LineAsset): ShoppingCartData['carrier'] {
  return {
    id: line.carrier_id!,
    name: line.carrier_name!,
    image_url: line.carrier_image_url
  };
}

export function calculateItemsToDelete(shopping_cart_items: ShoppingCartItem[]): number[] {
  return shopping_cart_items.reduce((acc: number[], item: ShoppingCartItem) => {
    if (item.type !== CART_ITEM_TYPE_ACCESSORY) acc.push(item.id as number);
    return acc;
  }, []);
}

export function removeDeviceToActivateParams(cart: ShoppingCartData): ShoppingCartData {
  return {
    ...cart,
    device_identifier: undefined,
    order_sim: undefined,
    new_sim: undefined,
    esim_activation: undefined,
    new_eid: undefined
  };
}

export function clearCartCondition(res: ShoppingCartData): number {
  const cart_data: string[] = Object.keys(res).filter(
    (key) => !['authorized_to_place_order', 'fields', 'shopping_cart_items'].includes(key)
  );
  return cart_data.length;
}

export function calculateBulkOrderItems(
  price_summary: OrderPriceSummary | undefined
): OrderPriceSummary | undefined {
  if (!price_summary) return undefined;

  price_summary.is_bulk_order = true;
  price_summary.bulk_order_additional_info!.bulk_accessories = [];
  price_summary.bulk_order_additional_info!.bulk_features = [];

  price_summary?.one_time_costs?.forEach((one_time: OrderPriceSummaryItem) => {
    switch (one_time.type) {
      case 'DEVICE':
        price_summary!.bulk_order_additional_info!.bulk_onetime_device = one_time;
        break;
      case 'ACCESSORY':
        price_summary!.bulk_order_additional_info!.bulk_accessories!.push(one_time);
        break;
      default:
        break;
    }
  });

  price_summary?.recurring_costs?.forEach((recurring: OrderPriceSummaryItem) => {
    switch (recurring.type) {
      case 'DEVICE':
        price_summary!.bulk_order_additional_info!.bulk_recurring_device = recurring;
        break;
      case 'PLAN':
        price_summary!.bulk_order_additional_info!.bulk_plan = recurring;
        break;
      case 'FEATURE':
        price_summary!.bulk_order_additional_info!.bulk_features!.push(recurring);
        break;
      default:
        break;
    }
  });

  return price_summary;
}
